import React, { useState } from 'react';

interface KidsaNewsLetterProps {
  subtitle?: string;
  title?: string;
  mailRoot?: string;
  emailPlaceholder?: string;
  subscribeButtonText?: string;
  planeShape?: string;
  pencilShape?: string;
  ctaShape?: string;
}

const KidsaNewsLetter: React.FC<KidsaNewsLetterProps> = ({
  subtitle,
  title,
  mailRoot,
  emailPlaceholder = 'Enter your email',
  subscribeButtonText = 'Subscribe',
  planeShape,
  pencilShape,
  ctaShape
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false });

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Método alternativo para enviar notificaciones cuando el endpoint no está disponible
  const simulateEmailNotification = async (subscriberEmail: string, targetEmail: string) => {
    try {
      // Estructura de datos para el correo
      const emailData = {
        to: targetEmail,
        from: 'notification@yourdomain.com',
        subject: '¡Nuevo suscriptor al boletín!',
        body: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
            <h2 style="color: #4a6cf7; margin-bottom: 20px;">¡Nuevo suscriptor!</h2>
            <p>Hola,</p>
            <p>Un nuevo usuario se ha suscrito a tu boletín:</p>
            <div style="background-color: #f9f9f9; padding: 15px; border-radius: 4px; margin: 15px 0;">
              <p><strong>Correo electrónico:</strong> ${subscriberEmail}</p>
              <p><strong>Fecha:</strong> ${new Date().toLocaleString()}</p>
            </div>
            <p>¡Gracias por usar nuestro servicio!</p>
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; font-size: 12px; color: #666;">
              <p>Este es un mensaje automático, por favor no responder directamente.</p>
            </div>
          </div>
        `
      };

      // Enviar el correo usando FormSubmit (no requiere API keys)
      const formsubmitEndpoint = `https://formsubmit.co/${targetEmail}`;
      
      const response = await fetch(formsubmitEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: 'Notificación de Suscripción',
          message: `Nuevo suscriptor: ${subscriberEmail}`,
          email: subscriberEmail
        })
      });
      
      if (!response.ok) {
        throw new Error('Error al enviar notificación por método alternativo');
      }
      
      console.log('Notificación enviada con éxito al correo:', targetEmail);
      
      // También guardamos en localStorage para referencia
      const savedNotifications = JSON.parse(localStorage.getItem('newsletterNotifications') || '[]');
      savedNotifications.push({
        timestamp: new Date().toISOString(),
        subscriber: subscriberEmail,
        targetEmail: targetEmail
      });
      localStorage.setItem('newsletterNotifications', JSON.stringify(savedNotifications));
      
      return true;
    } catch (error) {
      console.error('Error enviando notificación:', error);
      return false;
    }
  };

  // Handle form subscription
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ text: 'Please enter your email address', isError: true });
      return;
    }
  
    if (!isValidEmail(email)) {
      setMessage({ text: 'Please enter a valid email address', isError: true });
      return;
    }
  
    setIsSubmitting(true);
    setMessage({ text: '', isError: false });
  
    try {
      // Enviar la suscripción al endpoint actualizado
      const response = await fetch('https://backend.anubisai.net/api/mail/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          mailRoot
        }),
      });
  
      // Si hay un problema con el endpoint, intentar el método alternativo
      if (!response.ok) {
        console.warn('Endpoint no disponible, usando método alternativo');
        
        // Usar el método de simulación como respaldo
        if (mailRoot) {
          await simulateEmailNotification(email, mailRoot);
        }
        
        // Si el error es grave, lanzar una excepción para mostrar el mensaje de error
        if (response.status >= 500) {
          throw new Error('Subscription failed. Please try again later.');
        }
      }
  
      // Handle successful subscription
      setEmail('');
      setMessage({ text: 'Thank you for subscribing!', isError: false });
      
      // Simulamos una pausa para mostrar el mensaje de éxito
      setTimeout(() => {
        setMessage({ text: '', isError: false });
      }, 5000);
    } catch (error) {
      console.error('Subscription error:', error);
      setMessage({ 
        text: error instanceof Error ? error.message : 'Subscription failed. Please try again later.', 
        isError: true 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="main-cta-section">
      <div className="container">
        <div className="main-cta-wrapper section-padding">
          <div className="pencil-shape">
            <img src={pencilShape} alt="img" />
          </div>
          <div className="plane-shape float-bob-y">
            <img src={planeShape} alt="img" />
          </div>
          <div className="cta-shape float-bob-x">
            <img src={ctaShape} alt="img" />
          </div>
          <div className="cta-bg"></div>
          <div className="section-title text-center">
            <span className="text-white wow fadeInUp">{subtitle}</span>
            <h2 
              className="text-white wow fadeInUp" 
              data-wow-delay=".3s"
              dangerouslySetInnerHTML={{ __html: title }}
            />
          </div>
          <form onSubmit={handleSubscribe} className="newsletter-form">
            <div className="newsletter-items">
              <div className="form-clt wow fadeInUp" data-wow-delay=".5s">
                <input 
                  type="email" 
                  name="email" 
                  id="email" 
                  placeholder={emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <button 
                className="theme-btn wow fadeInUp" 
                data-wow-delay=".7s" 
                type="submit"
                disabled={isSubmitting}
              >
                <span>{isSubmitting ? 'Subscribing...' : subscribeButtonText}</span>
              </button>
            </div>
            {message.text && (
              <div className={`message-container ${message.isError ? 'error-alert' : 'success-alert'} mt-6 py-3 px-4 rounded-lg text-center transition-all duration-300 wow fadeIn`} data-wow-delay=".3s">
                <div className="flex items-center justify-center">
                  {message.isError ? (
                    <div>Error</div>
                  ) : (
                    <div>OK</div>
                  )}
                  <p className={`message-text ${message.isError ? 'text-red-500' : 'text-green-500'} font-medium`}>
                    {message.text}
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default KidsaNewsLetter;